import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, map, of, tap, switchMap, forkJoin } from 'rxjs';
import { FeatureFlag, LdProject, Rule, Environment, Member } from '../models/feature-flag-models';
import { UserProfileService } from '../../services/user-profile.service';

@Injectable({
  providedIn: 'root'
})
export class LaunchDarklyService {
  private readonly apiUrl = 'https://app.launchdarkly.com/api/v2';
  private environments: Environment[] = [];
  private currentProjectKey: string | null = null;

  constructor(
    @Inject(HttpClient) private readonly http: HttpClient,
    @Inject(UserProfileService) private readonly userProfileService: UserProfileService
  ) {}

  private getHeaders(apiKey: string): HttpHeaders {
    return new HttpHeaders({
      'Authorization': apiKey,
      'Content-Type': 'application/json'
    });
  }

  private getPatchHeaders(apiKey: string): HttpHeaders {
    return new HttpHeaders({
      'Authorization': apiKey,
      'Content-Type': 'application/json; domain-model=launchdarkly.semanticpatch'
    });
  }

  getProjects(apiKey: string): Observable<LdProject[]> {
    return this.http.get<{ items: LdProject[] }>(
      `${this.apiUrl}/projects?limit=100&sort=name`,
      { headers: this.getHeaders(apiKey) }
    ).pipe(
      map(response => response?.items || []),
      catchError(error => {
        console.error('Error fetching projects:', error);
        return of([]);
      })
    );
  }

  loadEnvironments(apiKey: string, projectKey: string): Observable<Environment[]> {
    return this.http.get<{ items: Environment[] }>(
      `${this.apiUrl}/projects/${projectKey}/environments`,
      { headers: this.getHeaders(apiKey) }
    ).pipe(
      map(response => response.items),
      tap(environments => {
        this.environments = environments;
        this.currentProjectKey = projectKey;
      }),
      catchError(error => {
        console.error('Error fetching environments:', error);
        return of([]);
      })
    );
  }

  getFeatureFlags(apiKey: string, projectKey: string): Observable<FeatureFlag[]> {
    // Helper function to get a page of flags
    const getPage = (offset: number): Observable<{items: FeatureFlag[], totalCount: number}> => {
      return this.http.get<{items: FeatureFlag[], totalCount: number}>(
        `${this.apiUrl}/flags/${projectKey}?sort=name&limit=100&offset=${offset}`,
        { headers: this.getHeaders(apiKey) }
      );
    };

    // Start with first page
    return getPage(0).pipe(
      switchMap(firstPage => {
        // If we have all items, return them
        if (firstPage.items.length >= firstPage.totalCount) {
          return of(firstPage.items);
        }

        // Calculate how many additional pages we need
        const totalPages = Math.ceil(firstPage.totalCount / 100);
        const remainingPages = Array.from(
          { length: totalPages - 1 },
          (_, i) => getPage((i + 1) * 100)
        );

        // Fetch all remaining pages
        return forkJoin(remainingPages).pipe(
          map(additionalPages => {
            // Combine all pages' items with the first page
            const allFlags = [
              ...firstPage.items,
              ...additionalPages.flatMap(page => page.items)
            ];
            return allFlags;
          })
        );
      }),
      // Get detailed flag data for each flag
      switchMap(flags => {
        if (!flags.length) return of([]);

        // Create an array of observables for each flag's detailed data
        const flagObservables = flags.map(flag =>
          this.getFeatureFlag(apiKey, projectKey, flag.key).pipe(
            map(detailedFlag => detailedFlag ?? flag), // fallback to original flag if detailed fetch fails
            catchError(() => of(flag)) // fallback to original flag on error
          )
        );

        // Wait for all flag details to be fetched
        return forkJoin(flagObservables);
      }),
      catchError(error => {
        console.error('Error in feature flags pipeline:', error);
        return of([]);
      })
    );
  }

  getFeatureFlag(apiKey: string, projectKey: string, flagKey: string): Observable<FeatureFlag | undefined> {
    return this.http.get<FeatureFlag>(
      `${this.apiUrl}/flags/${projectKey}/${flagKey}`,
      { headers: this.getHeaders(apiKey) }
    ).pipe(
      map(response => response),
      catchError(error => {
        console.error('Error in feature flags pipeline:', error);
        return of(undefined);
      })
    );
  }

  updateFeatureFlagRules(apiKey: string, flagKey: string, environment: string, rules: Rule[]): Observable<any> {
    if (!this.currentProjectKey) {
      console.error('No project selected');
      return of(null);
    }

    const envKey = this.environments.find(env => env.name.toLowerCase() === environment.toLowerCase())?.key;
    if (!envKey) {
      console.error('Environment not found:', environment);
      return of(null);
    }

    const url = `${this.apiUrl}/flags/${this.currentProjectKey}/${flagKey}`;
    
    // Get user email from UserProfileService
    return this.userProfileService.getUserEmail().pipe(
      switchMap(userEmail => {
        // Create instructions for each clause update
        const instructions = rules.flatMap(rule =>
          rule.clauses.map(clause => ({
            kind: 'updateClause',
            ruleId: rule._id,
            clauseId: clause._id,
            clause: clause,
          }))
        );

        const body = {
          comment: `Updated targeting rules in ${environment} environment by ${userEmail}`,
          environmentKey: envKey,
          instructions
        };

        return this.http.patch(url, body, { headers: this.getPatchHeaders(apiKey) });
      }),
      catchError(error => {
        console.error('Error updating feature flag rules:', error);
        throw error;
      })
    );
  }

  getMember(apiKey: string, memberId: string): Observable<Member | null> {
    return this.http.get<Member>(
      `${this.apiUrl}/members/${memberId}`,
      { headers: this.getHeaders(apiKey) }
    ).pipe(
      catchError(error => {
        console.error('Error fetching member details:', error);
        return of(null);
      })
    );
  }

  toggleFeatureFlag(
    apiKey: string,
    projectKey: string,
    flagKey: string,
    environment: string,
    enabled: boolean
  ): Observable<any> {
    // Validate that we have the environment loaded
    const envKey = this.environments.find(env => env.name.toLowerCase() === environment.toLowerCase())?.key;
    if (!envKey) {
      console.error('Environment not found:', environment);
      return of(null);
    }

    const url = `${this.apiUrl}/flags/${projectKey}/${flagKey}`;
    
    // Get user email from UserProfileService
    return this.userProfileService.getUserEmail().pipe(
      switchMap(userEmail => {
        const body = {
          comment: `${enabled ? 'Enabling' : 'Disabling'} flag in ${environment} environment by ${userEmail}`,
          environmentKey: envKey,
          instructions: [
            {
              kind: enabled ? 'turnFlagOn' : 'turnFlagOff'
            }
          ]
        };

        return this.http.patch(url, body, { headers: this.getPatchHeaders(apiKey) });
      }),
      catchError(error => {
        console.error('Error toggling feature flag:', error);
        throw error;
      })
    );
  }
}
